import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  Table,
  Typography,
  Tag,
  Space,
  Button,
  Statistic,
  Modal,
  Form,
  Input,
  Select,
  Alert,
  message as messageApi,
} from "antd";
import { BellOutlined, ReloadOutlined, SendOutlined } from "@ant-design/icons";
import { apiService } from "@/services/api";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

/** Une diffusion telle que le serveur la regroupe. */
interface Diffusion {
  type: string;
  title: string;
  message: string;
  recipients: number;
  readCount: number;
  sentAt: string | null;
}

interface Totaux {
  total: number;
  read: number;
  unread: number;
  readRate: number | null;
  recipients: number;
}

/** Libellés des types, tels que les nomme `NotificationType` côté serveur. */
const libellesType: Record<string, string> = {
  NEW_CONTENT: "Nouveau contenu",
  NEW_EPISODE: "Nouvel épisode",
  SUBSCRIPTION: "Abonnement",
  RECOMMENDATION: "Recommandation",
  PROMOTION: "Promotion",
  SYSTEM: "Système",
};

const couleursType: Record<string, string> = {
  NEW_CONTENT: "green",
  NEW_EPISODE: "cyan",
  SUBSCRIPTION: "blue",
  RECOMMENDATION: "purple",
  PROMOTION: "orange",
  SYSTEM: "default",
};

function formaterDate(valeur: string | null): string {
  if (!valeur) return "—";
  const date = new Date(valeur);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("fr-FR");
}

/**
 * Page « Notifications » du backoffice.
 *
 * Elle appelait auparavant `/notifications`, qu'aucun contrôleur ne servait :
 * chaque appel tombait en 404. Ses indicateurs étaient de surcroît inventés —
 * « E-mails envoyés », « Planifiées » — alors qu'il n'existe ni canal e-mail ni
 * planification. Elle s'appuie désormais sur ce que le service fait réellement :
 * diffuser une notification aux abonnés, et rendre compte des lectures.
 */
export const NotificationList: React.FC = () => {
  const [diffusions, setDiffusions] = useState<Diffusion[]>([]);
  const [totaux, setTotaux] = useState<Totaux | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  const [typesDisponibles, setTypesDisponibles] = useState<string[]>([]);
  const [formulaireOuvert, setFormulaireOuvert] = useState(false);
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  const [form] = Form.useForm();

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const [liste, stats] = await Promise.all([
        apiService.getBroadcasts(1, 50),
        apiService.getNotificationStats(),
      ]);
      setDiffusions(liste.data ?? []);
      setTotaux(stats);
    } catch (e: any) {
      setErreur(
        e?.response?.data?.error ??
          "Impossible de charger les notifications. Le service est peut-être indisponible."
      );
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    charger();
    apiService
      .getNotificationTypes()
      .then(setTypesDisponibles)
      .catch(() => setTypesDisponibles([]));
  }, [charger]);

  const diffuser = async () => {
    const valeurs = await form.validateFields();
    setEnvoiEnCours(true);
    try {
      const resultat = await apiService.broadcastNotification({
        type: valeurs.type,
        title: valeurs.title,
        message: valeurs.message,
        contentId: valeurs.contentId ? Number(valeurs.contentId) : null,
      });
      messageApi.success(
        `Notification envoyée à ${resultat.recipients} destinataire(s)`
      );
      setFormulaireOuvert(false);
      form.resetFields();
      charger();
    } catch (e: any) {
      messageApi.error(
        e?.response?.data?.error ?? "La diffusion n'a pas abouti"
      );
    } finally {
      setEnvoiEnCours(false);
    }
  };

  const colonnes = [
    {
      title: "TITRE",
      dataIndex: "title",
      key: "title",
      render: (titre: string, ligne: Diffusion) => (
        <Space direction="vertical" size={0}>
          <Text strong>{titre}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {ligne.message}
          </Text>
        </Space>
      ),
    },
    {
      title: "TYPE",
      dataIndex: "type",
      key: "type",
      width: 170,
      render: (type: string) => (
        <Tag color={couleursType[type] ?? "default"}>
          {libellesType[type] ?? type}
        </Tag>
      ),
    },
    {
      title: "DESTINATAIRES",
      dataIndex: "recipients",
      key: "recipients",
      width: 140,
      render: (nombre: number) => <Text>{nombre}</Text>,
    },
    {
      title: "LUES",
      key: "readCount",
      width: 140,
      render: (_: unknown, ligne: Diffusion) => {
        const taux =
          ligne.recipients > 0
            ? Math.round((ligne.readCount * 100) / ligne.recipients)
            : null;
        return (
          <Text type={taux !== null && taux >= 50 ? "success" : undefined}>
            {ligne.readCount}
            {taux !== null ? ` (${taux} %)` : ""}
          </Text>
        );
      },
    },
    {
      title: "ENVOYÉE LE",
      dataIndex: "sentAt",
      key: "sentAt",
      width: 190,
      render: (valeur: string | null) => (
        <Text type="secondary">{formaterDate(valeur)}</Text>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 16,
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
        }}
      >
        <div>
          <Title level={2} style={{ margin: 0, marginBottom: 8 }}>
            Notifications
          </Title>
          <Text type="secondary">
            Diffusions envoyées aux abonnés de l'application
          </Text>
        </div>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={charger}>
            Actualiser
          </Button>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => setFormulaireOuvert(true)}
          >
            Nouvelle diffusion
          </Button>
        </Space>
      </div>

      {erreur && (
        <Alert
          type="error"
          showIcon
          message="Notifications indisponibles"
          description={erreur}
          style={{ marginBottom: 24 }}
        />
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <Card>
          <Statistic
            title="Notifications envoyées"
            value={totaux?.total ?? 0}
            prefix={<BellOutlined style={{ color: "#1890ff" }} />}
          />
        </Card>
        <Card>
          <Statistic title="Destinataires" value={totaux?.recipients ?? 0} />
        </Card>
        <Card>
          {/* Nul plutôt que 0 % quand rien n'a été envoyé : afficher 0 %
              laisserait croire que personne ne lit. */}
          <Statistic
            title="Taux de lecture"
            value={totaux?.readRate ?? 0}
            suffix={totaux?.readRate === null ? "" : "%"}
            formatter={(v) =>
              totaux?.readRate === null ? "—" : String(v)
            }
          />
        </Card>
        <Card>
          <Statistic title="Non lues" value={totaux?.unread ?? 0} />
        </Card>
      </div>

      <Card>
        <Table
          columns={colonnes}
          dataSource={diffusions}
          loading={chargement}
          rowKey={(ligne) => `${ligne.type}-${ligne.title}-${ligne.sentAt}`}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          locale={{
            emptyText:
              "Aucune notification envoyée pour l'instant. Une diffusion part aussi automatiquement à chaque nouveau contenu publié.",
          }}
          scroll={{ x: 800 }}
        />
      </Card>

      <Modal
        title="Nouvelle diffusion"
        open={formulaireOuvert}
        onCancel={() => setFormulaireOuvert(false)}
        onOk={diffuser}
        confirmLoading={envoiEnCours}
        okText="Envoyer"
        cancelText="Annuler"
      >
        <Paragraph type="secondary" style={{ fontSize: 13 }}>
          La notification part à <strong>tous les abonnés</strong> ayant activé
          la préférence correspondante, et ne peut pas être rappelée. Le push
          n'atteint que les appareils enregistrés ; la notification dans
          l'application est créée pour tout le monde.
        </Paragraph>
        <Form form={form} layout="vertical" initialValues={{ type: "SYSTEM" }}>
          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true, message: "Choisissez un type" }]}
          >
            <Select>
              {(typesDisponibles.length > 0
                ? typesDisponibles
                : Object.keys(libellesType)
              ).map((type) => (
                <Option key={type} value={type}>
                  {libellesType[type] ?? type}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="title"
            label="Titre"
            rules={[{ required: true, message: "Le titre est obligatoire" }]}
          >
            <Input maxLength={80} showCount />
          </Form.Item>
          <Form.Item
            name="message"
            label="Message"
            rules={[{ required: true, message: "Le message est obligatoire" }]}
          >
            <Input.TextArea rows={3} maxLength={200} showCount />
          </Form.Item>
          <Form.Item
            name="contentId"
            label="Contenu associé (facultatif)"
            tooltip="Identifiant du film ou de la série à ouvrir au clic sur la notification"
          >
            <Input type="number" placeholder="ex. 8" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default NotificationList;
